import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { useAuth } from '../auth/AuthContext'
import { LoadingBlock, ErrorState } from '../components/States'
import { Answer } from '../components/Answer'
import { useToast } from '../components/Toasts'
import { onProgress, howFar } from '../lib/matchWatch'
import type { MatchProgress, MatchTurn } from '../types'

/**
 * One box that prices a role, and adds nothing to the library.
 *
 * **Three minutes of nothing is the whole problem this screen solves.**
 * Somebody who cannot tell "working" from "stuck" reloads the page, and
 * reloading throws away the minute they already waited. So the stage, the
 * clock, the moving bar and the answer-as-it-is-written are not decoration:
 * each one is there to answer "is this still going?".
 *
 * **The clock is the only thing we can always be honest about**, so it keeps
 * running even when nothing else has come back.
 *
 * Nothing on this screen says what a match costs us. Not an estimate, not a
 * running total. The routes behind it do not return one either, so there is
 * nothing here to accidentally show.
 */

/**
 * Where the conversation's id is remembered, per company.
 *
 * Per company because a shared browser would otherwise hand one person's id to
 * the next. The route refuses it anyway, since the record is not under their
 * company, but relying on that would mean every sign-in started by asking for
 * somebody else's conversation.
 */
const remembered = (tenant: string) => `stello.matchChat.${tenant}`

/**
 * Reading and writing what the browser remembers, where it may not work.
 *
 * Storage can be empty, refuse to write, or throw outright in a private
 * window. Every call here is wrapped, and a browser that refuses behaves the
 * way the first build did: a new conversation each time, which is worse but
 * not broken.
 */
const recall = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}
const keep = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value)
  } catch { /* a browser that will not remember is not a reason to fail */ }
}
const forget = (key: string) => {
  try {
    window.localStorage.removeItem(key)
  } catch { /* as above */ }
}

/** How often the screen asks how the match is going. */
const ASK_EVERY_MS = 3000

/**
 * How many failed checks in a row are tolerated before saying so.
 *
 * One failed check means nothing, probably a blip. Several in a row means
 * something real. Giving up on the first would make a working match look
 * broken; never giving up would spin forever.
 */
const LOSE_CONTACT_AFTER = 5

/** When the screen stops claiming it is working and offers to ask again. */
const PAST_WAITING_S = 10 * 60

/** The four questions everybody asks after a match. */
const USUAL = [
  'Why this match?',
  'Show alternatives',
  'Total comp?',
  'One-line range',
]

const SAMPLE =
  'Senior Regulatory Affairs Consultant\n\n'
  + 'Responsibilities:\n'
  + '- Lead regulatory submissions for new and existing products across the US and EU\n'
  + '- Act as the main point of contact with health authorities during review\n'
  + '- Review promotional material for regulatory compliance before release\n'
  + '- Advise development teams on regulatory strategy from early phase onward\n\n'
  + 'Requirements:\n'
  + '- 8+ years in regulatory affairs within pharmaceuticals or medical devices\n'
  + '- Direct experience of FDA and EMA submissions\n'
  + '- No direct reports; works through influence across project teams'

export function MatchAJob() {
  const toast = useToast()
  const { me } = useAuth()
  const key = remembered(me?.tenant?.slug ?? 'unknown')
  const [hasSurvey, setHasSurvey] = useState<boolean | null>(null)
  const [opening, setOpening] = useState<string | null>(null)

  const [chatId, setChatId] = useState<string | null>(null)
  const [turns, setTurns] = useState<MatchTurn[]>([])
  const [text, setText] = useState('')
  const [going, setGoing] = useState<MatchProgress | null>(null)
  const [sending, setSending] = useState(false)
  const [lostContact, setLostContact] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [saving, setSaving] = useState(false)
  /** The draft this conversation has already produced, so there is only ever one. */
  const [saved, setSaved] = useState<string | null>(null)

  const live = useRef(true)
  const misses = useRef(0)
  const foot = useRef<HTMLDivElement | null>(null)
  const box = useRef<HTMLTextAreaElement | null>(null)

  /*
   * Set true on the way in as well as false on the way out.
   *
   * React mounts, unmounts and remounts a component in development, so a
   * cleanup that only ever sets this false leaves it false for good, and every
   * answer that comes back afterwards is thrown away by the guards below. The
   * screen then shows what was sent and nothing else, forever.
   */
  /*
   * Whether there is still something to wait for.
   *
   * Declared above the effects that read it, because they are easier to read
   * when the thing they turn on is already in front of you. Stopped counts as
   * finished: nothing is going to arrive, and it is not a failure either.
   */
  const busy = going !== null
    && going.state !== 'done'
    && going.state !== 'failed'
    && going.state !== 'stopped'

  useEffect(() => {
    live.current = true
    return () => { live.current = false }
  }, [])

  /**
   * Pulls the finished answer onto the screen.
   *
   * **An answer that arrives before the first check used to never appear.**
   * Only the repeating check fetched the conversation, and it only runs
   * while something is still going. A full match takes half a minute, so the
   * first check says "matching" and the loop starts. Asking what else was
   * close is answered from words the match already wrote, with no trip to
   * the AI at all, so it is finished before that first check comes back: the
   * loop never starts, nothing fetches the conversation, and the question
   * sits on screen with no answer under it until the tab is left and
   * reopened.
   *
   * So finishing pulls the answer, wherever the finish is noticed.
   */
  const pullAnswer = useCallback(async (id: string) => {
    try {
      const chat = await api.matchChat(id)
      if (live.current) setTurns(chat.turns)
    } catch {
      /*
       * The answer is saved either way, and the next check or the next visit
       * will show it. Saying so here would put an error on a screen whose
       * match actually worked.
       */
    }
  }, [])

  /*
   * Opening the screen: is there anything to match against, and which
   * conversation are we in.
   *
   * The conversation is picked up rather than replaced. The first build
   * started a new one every time this screen mounted, so leaving the tab and
   * coming back threw away whatever was there, with no warning and no way to
   * get it back. New chat is the only thing that clears it, which is what that
   * button has always meant.
   */
  useEffect(() => {
    let stop = false
    void (async () => {
      try {
        const { hasSurvey: has } = await api.matchOverview()
        if (stop) return
        setHasSurvey(has)
        if (!has) return

        const saved = recall(key)
        if (saved) {
          try {
            const chat = await api.matchChat(saved)
            if (stop) return
            setChatId(chat.chatId)
            setTurns(chat.turns)
            // Picked up where it was left. Somebody who waited two minutes,
            // went to check something and came back should not start over.
            if (chat.running) {
              const progress = await api.matchProgress(chat.running)
              if (!stop) setGoing(progress)
            }
            return
          } catch {
            /*
             * Gone, expired, or from another company. Not a fault anybody
             * needs telling about: a day-old conversation ending is the normal
             * way one ends. A fresh one starts below.
             */
            forget(key)
          }
        }

        const chat = await api.startMatchChat()
        if (stop) return
        setChatId(chat.chatId)
        keep(key, chat.chatId)
      } catch (err) {
        if (!stop) setOpening(err instanceof Error ? err.message : 'That did not open.')
      }
    })()
    return () => { stop = true }
  }, [key])

  /*
   * Asking how it is going, every few seconds until it is done.
   *
   * A few failed checks in a row are tolerated rather than shown, because one
   * failure means nothing and showing it would make a working match look
   * broken. Several in a row is real and is said.
   */
  useEffect(() => {
    if (!going || !busy) return
    const id = setInterval(() => {
      void (async () => {
        try {
          const next = await api.matchProgress(going.matchId)
          if (!live.current) return
          misses.current = 0
          setLostContact(false)
          onProgress(next, {
            keep: setGoing,
            // The answer is on the chat now, which is what the download saves.
            pull: () => { if (chatId) void pullAnswer(chatId) },
          })
        } catch {
          misses.current += 1
          if (live.current && misses.current >= LOSE_CONTACT_AFTER) setLostContact(true)
        }
      })()
    }, ASK_EVERY_MS)
    return () => clearInterval(id)
  }, [going, chatId, pullAnswer])

  // The clock, which keeps running whatever else has or has not come back.
  const [, tick] = useState(0)
  useEffect(() => {
    if (!going || !busy) return
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [going])

  useEffect(() => {
    foot.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns.length, going?.state])

  /*
   * The box grows with what is in it, up to a point.
   *
   * A pasted job description is fifteen lines and a fixed three-line box
   * showed the first two and a half of them, clipped mid-sentence, with no
   * way to see the rest. Somebody checking they pasted the right role could
   * not.
   *
   * Capped, because past about a third of the screen the box is pushing the
   * conversation out of view to show text the person has already read.
   */
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 280)}px`
  }, [text])

  const send = async (what: string) => {
    const body = what.trim()
    if (body === '' || !chatId || busy || sending) return
    setSending(true)
    setShowDetail(false)
    try {
      const started = await api.sendToMatch(chatId, body)
      if (started.alreadyGoing) {
        toast.fail(started.message ?? 'A match is already running in this chat.')
        return
      }
      setText('')
      /*
       * Shown as sent before any answer exists.
       *
       * The chat on the server does not carry this turn until it has been
       * answered, and a person who pressed send needs to see what they sent
       * straight away rather than after the first check comes back.
       */
      setTurns((old) => [...old, {
        turnId: `pending_${started.matchId}`,
        asked: body,
        askedAt: new Date().toISOString(),
        answer: '',
        answeredAt: null,
        readAs: null,
        note: '',
      }])
      const first = await api.matchProgress(started.matchId)
      if (!live.current) return
      /*
       * Through the same rule as the repeating check, because a question
       * answered from the match's own words is already finished by the time
       * this first look comes back, and nothing will look again.
       */
      onProgress(first, { keep: setGoing, pull: () => void pullAnswer(chatId) })
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not send.')
    } finally {
      setSending(false)
    }
  }

  /*
   * Stops watching, and says so.
   *
   * The match itself carries on at the back: it is already on the queue and
   * nothing can recall it, and pretending otherwise would be a lie on a
   * screen built to be honest about waiting. What stops is this screen
   * waiting for it, which is the thing the person actually wanted.
   */
  const stop = async () => {
    if (!going) return
    setStopping(true)
    try {
      await api.stopMatch(going.matchId)
      setGoing(null)
      // The question goes with it, so the conversation reads as though it was
      // never asked. A stopped match keeps nothing.
      setTurns((old) => old.filter((t) => !t.turnId.startsWith('pending_')))
      toast.ok('Stopped. Nothing was saved, so asking again is safe.')
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not stop.')
    } finally {
      setStopping(false)
    }
  }

  const newChat = async () => {
    if (!chatId) return
    try {
      await api.forgetMatchChat(chatId)
      const chat = await api.startMatchChat()
      setChatId(chat.chatId)
      keep(key, chat.chatId)
      setTurns([])
      setGoing(null)
      setSaved(null)
      setText('')
      setLostContact(false)
      toast.ok('Started a new chat. The last one is forgotten.')
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not clear.')
    }
  }

  /*
   * The download, built here from the conversation on the screen.
   *
   * Not from a request. It cannot leak what a match costs us because the
   * browser has never been told, it can only ever hold this company's own
   * chat because it holds the chat in front of the person, and it writes
   * nothing into a log group.
   */
  const download = async () => {
    const answered = turns.filter((t) => t.answeredAt !== null)
    if (answered.length === 0) return
    try {
      // Fetched when the button is pressed, so nobody who never downloads
      // pays for the PDF library.
      const [{ transcriptBlocks, transcriptName }, { renderPdf }] = await Promise.all([
        import('../lib/transcript'), import('../lib/pdf'),
      ])
      const name = transcriptName(answered)
      await renderPdf(transcriptBlocks(answered), name)
      toast.ok(`Downloaded ${name}`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'The download did not start.')
    }
  }

  /*
   * Keeping the pasted job description.
   *
   * Offered only where a full job description was pasted, because a title on
   * its own would make a role with a title and nothing else, sitting in the
   * library looking finished while somebody still has to write it.
   *
   * The draft is a new role with a new id. What was pasted keeps its own id
   * and expires with the conversation; this one is in the library and is
   * deleted the way a role is deleted.
   */
  const keepAsDraft = async () => {
    if (!chatId || saving || saved) return
    setSaving(true)
    try {
      const made = await api.saveMatchAsDraft(chatId)
      setSaved(made.id)
      toast.ok(`Saved "${made.title}" as a draft. It is in your library, not published.`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  const copy = async (answer: string) => {
    try {
      await navigator.clipboard.writeText(answer)
      toast.ok('Answer copied')
    } catch {
      toast.fail('That did not copy. Select the answer and copy it by hand.')
    }
  }

  if (opening) return <div className="screen screen--narrow"><ErrorState message={opening} /></div>
  if (hasSurvey === null) return <div className="screen screen--narrow"><LoadingBlock /></div>

  /*
   * A company with no market data never gets to the chat.
   *
   * The same explanation Benchmarking gives them. A box that cannot answer is
   * worse than no tab at all.
   */
  if (!hasSurvey) {
    return (
      <div className="screen screen--narrow" style={{ paddingTop: 28 }}>
        <h1>Match a job</h1>
        <p className="subline">
          There is no market data here to match against yet. Add a salary survey first, and
          then this can tell you what a role is worth.
        </p>
        <a className="btn btn--primary" href="/market-data/add">Add a survey</a>
      </div>
    )
  }

  const answered = turns.filter((t) => t.answeredAt !== null)
  const lastAnswered = answered[answered.length - 1]

  return (
    <div className={`screen screen--chat${turns.length === 0 && !busy ? ' chat--empty' : ''}`}>
      {/*
        The explanation is for a new chat. Once there is a conversation it has
        been read, and keeping it costs a third of a short window that the
        answer needs more, so the heading slims to its title the way every
        assistant's does.
      */}
      <header className={`chat__head${turns.length > 0 ? ' chat__head--slim' : ''}`}>
        <h1>Match a job</h1>
        {turns.length === 0 && (
          <>
            <p>
              Paste a job description, or just type a job title, and it gets matched against the
              market data this company holds, coming back with a job code, a level and what the
              market pays.
            </p>
            <p className="subline">
              A match takes 30 seconds to 3 minutes. A full job description gives a better answer
              than a title on its own.
            </p>
          </>
        )}
      </header>

      <div className="chat__thread">
        {turns.map((turn) => (
          <div key={turn.turnId} className="chat__turn">
            <div className="chat__asked">
              <pre>{turn.asked}</pre>
            </div>

            {turn.answeredAt !== null && (
              <div className="chat__answer">
                <Answer text={turn.answer} />
                <div className="actions">
                  <button type="button" className="btn btn--sm" onClick={() => void copy(turn.answer)}>
                    Copy answer
                  </button>
                  {/*
                    Only beside an answer that read a full job description. A
                    question, or a bare title, has nothing worth keeping. Not
                    beside a paste of several jobs either: this saves one
                    role, and which of them it saved would be a guess.
                  */}
                  {turn.readAs === 'description' && !((turn.jobs ?? 1) > 1) && (saved
                    ? <span className="subline">Saved as a draft in your job descriptions</span>
                    : (
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => void keepAsDraft()}
                        disabled={saving}
                      >
                        {saving ? 'Saving' : 'Save as a draft job description'}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {busy && going && (
          <Working
            going={going}
            lostContact={lostContact}
            onStop={() => void stop()}
            stopping={stopping}
          />
        )}

        {going?.state === 'failed' && (
          <div className="panel panel--bad">
            <p>{going.failedReason}</p>
            {going.failedDetail && (
              <details open={showDetail} onToggle={(e) => setShowDetail(e.currentTarget.open)}>
                <summary>Technical detail</summary>
                <pre>{going.failedDetail}</pre>
              </details>
            )}
            <button type="button" className="btn btn--primary" onClick={() => void send(lastAsked(turns))}>
              Try again
            </button>
          </div>
        )}

        {/* Under the most recent answer only, so old answers do not grow buttons. */}
        {lastAnswered && !busy && lastAnswered.readAs !== 'not-a-job' && (
          <div className="chat__next">
            {USUAL.map((q) => (
              <button key={q} type="button" className="btn btn--sm" onClick={() => void send(q)}>
                {q}
              </button>
            ))}
          </div>
        )}

        <div ref={foot} />
      </div>

      <form
        className="chat__box"
        onSubmit={(e) => { e.preventDefault(); void send(text) }}
      >
        <div className="chat__field">
          <textarea
            ref={box}
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste a job description, or type a job title, or ask a follow-up about the last match"
            aria-label="What to match"
            onKeyDown={(e) => {
              // Enter sends. Shift and Enter starts a new line, which is what
              // a box somebody pastes four paragraphs into has to allow.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void send(text)
              }
            }}
          />
          <div className="chat__tools">
            <button type="button" className="btn btn--sm" onClick={() => setText(SAMPLE)}>
              Use a sample JD
            </button>
            {turns.length > 0 && (
              <button type="button" className="btn btn--sm" onClick={() => void newChat()}>
                New chat
              </button>
            )}
            {/* Nothing to save until something has been answered. */}
            {answered.length > 0 && (
              <button type="button" className="btn btn--sm" onClick={() => void download()}>
                Download conversation
              </button>
            )}
            {/* Pushes Send to the right, so the row reads actions then send. */}
            <span className="chat__gap" />
            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || sending || text.trim() === ''}
            >
              Send
            </button>
          </div>
        </div>
        {/*
          Under the field rather than in the row with the buttons. In the row
          it took 230 pixels of a 720 pixel column and pushed Send onto a line
          of its own.
        */}
        <p className="chat__hint">Enter sends. Shift and Enter starts a new line.</p>
      </form>
    </div>
  )
}

/** The last thing asked, so Try again asks it again rather than nothing. */
const lastAsked = (turns: MatchTurn[]) => turns[turns.length - 1]?.asked ?? ''

/**
 * What the screen says while it works.
 *
 * The stage, the clock and the answer so far. The bar moves on the stage
 * rather than on a guessed percentage, because a bar that claims 60% and sits
 * there is worse than one that moves when something actually happens.
 */
function Working({
  going, lostContact, onStop, stopping,
}: {
  going: MatchProgress
  lostContact: boolean
  onStop: () => void
  stopping: boolean
}) {
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(going.startedAt)) / 1000))
  const far = howFar(going)
  const past = seconds > PAST_WAITING_S

  return (
    /*
     * Announced politely, and only the new words.
     *
     * `polite` so an update never cuts across what somebody is reading.
     * `aria-atomic="false"` so a screen reader hears what has just arrived
     * rather than the whole answer again on every change, which is what makes
     * a streaming answer unusable with a screen reader.
     */
    <div className="chat__working" aria-live="polite" aria-atomic="false">
      <div className="progress">
        <div className="progress__bar" style={{ width: `${far}%` }} />
      </div>
      <p>
        <strong>{going.stage}</strong> · {howLongSince(seconds)}
      </p>

      {past ? (
        <p className="subline">
          This has been going a while and may still be running. You can wait, or start it again.
        </p>
      ) : (
        <p className="subline">
          {seconds > 120
            ? 'Longer job descriptions take longer, and a benchmark run sharing the same AI '
              + 'allowance slows this down. Still waiting.'
            : 'You can leave this page. The match carries on and the answer is here when you come back.'}
        </p>
      )}

      {lostContact && (
        <p className="subline">
          We have lost contact with this match. It may still be running. Reload the page to pick
          it up again.
        </p>
      )}

      {/*
        Stopping belongs to the thing that is running, not to the thing being
        typed, so it sits here rather than in the composer. Somebody who
        pasted the wrong role should not have to sit through a match they no
        longer want.
      */}
      <div className="actions">
        <button type="button" className="btn btn--sm" onClick={onStop} disabled={stopping}>
          {stopping ? 'Stopping' : 'Stop'}
        </button>
      </div>

      {going.answerSoFar.trim() !== '' && (
        <div className="chat__sofar">
          <Answer text={going.answerSoFar} />
        </div>
      )}
    </div>
  )
}

function howLongSince(seconds: number): string {
  if (seconds < 60) return `${seconds} seconds so far`
  const mins = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${mins} minute${mins === 1 ? '' : 's'} ${rest} second${rest === 1 ? '' : 's'} so far`
}
